import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client/react';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  BackgroundJob,
  TaskScopeNameEnum,
  useEdmDocumentationCategoriesSearchListLazyQuery,
  useEdmDocumentationImportCreateMutation,
  useSectorsSearchListLazyQuery,
  useSitesSearchListLazyQuery,
  useUsersSearchListLazyQuery,
  useAssetsSearchListLazyQuery,
  useSiteAreasSearchListLazyQuery,
  useFormTemplatesSearchListLazyQuery,
  useTasksSearchListLazyQuery,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import { GetLoadOptions } from '@lib/components/Select/types';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import { showGraphQLErrors, getGraphQLErrors } from '@lib/utils/graphQLErrors';
import toast from '@lib/components/Toast/Toast';
import { APP_URLS } from 'constants/urls';
import { EventBusEvents, jobEventBus } from '@lib/utils/eventBus';
import { getDocumentationCategoriesLoadOptions } from 'utils/fetch/searchLists/documentationCategoriesSearchList';
import { getSectorsLoadOptions } from 'utils/fetch/searchLists/sectorsSearchList';
import { getSitesLoadOptions } from 'utils/fetch/searchLists/sitesSearchList';
import { getUsersLoadOptions } from 'utils/fetch/searchLists/usersSearchList';
import { getAssetsLoadOptions } from 'utils/fetch/searchLists/assetsSearchList';
import { getSiteAreasLoadOptions } from 'utils/fetch/searchLists/siteAreasSearchList';
import { getFormTemplatesLoadOptions } from 'utils/fetch/searchLists/formTemplatesSearchList';
import { getTasksLoadOptions } from 'utils/fetch/searchLists/tasksSearchList';
import { useSharedAiBudget } from 'components/AiAssistant/AiBudgetContext';
import createTaskGroupKeyResolver from '../../utils/createTaskGroupKeyResolver';
import getPreparedImportData from './getPreparedImportData';

export default function useDocumentImportActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const getTaskGroupKey = useMemo(
    () => createTaskGroupKeyResolver(apolloClient),
    [apolloClient],
  );

  const onCancel = useCallback(() => {
    navigate(APP_URLS.dashboard.documentation.index.getPathWithQuery());
  }, [navigate]);

  // Lazy queries for async selects
  const [fetchCategories] = useEdmDocumentationCategoriesSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchSectors] = useSectorsSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchSites] = useSitesSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchUsers] = useUsersSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchAssets] = useAssetsSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchAreas] = useSiteAreasSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchFormTemplates] = useFormTemplatesSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchTasks] = useTasksSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });

  const categoriesLoadOptions: GetLoadOptions = useCallback(
    () => getDocumentationCategoriesLoadOptions(fetchCategories),
    [fetchCategories],
  );
  const sectorsLoadOptions: GetLoadOptions = useCallback(
    () => getSectorsLoadOptions(fetchSectors),
    [fetchSectors],
  );
  const sitesLoadOptions: GetLoadOptions = useCallback(
    () => getSitesLoadOptions(fetchSites, undefined, { includeSector: true }),
    [fetchSites],
  );
  const approverLoadOptions = useCallback(
    (name, values) => {
      const categoryId = _get<string>(
        values,
        [DocumentFormFieldNames.Category, 'value'],
        '',
      );
      return getUsersLoadOptions(fetchUsers, {
        custom: {
          byCore: {
            onlyActive: true,
            access: {
              component: AccessComponentEnum.DocumentationManagement,
              level: AccessLevelEnum.Write,
              sourceableId: categoryId?.length ? categoryId : undefined,
            },
          },
        },
      });
    },
    [fetchUsers],
  );
  const equipmentLoadOptions: GetLoadOptions = useCallback(
    () => getAssetsLoadOptions(fetchAssets),
    [fetchAssets],
  );
  const areasLoadOptions: GetLoadOptions = useCallback(
    () => getSiteAreasLoadOptions(fetchAreas),
    [fetchAreas],
  );
  const formTemplatesLoadOptions: GetLoadOptions = useCallback(
    () => getFormTemplatesLoadOptions(fetchFormTemplates),
    [fetchFormTemplates],
  );
  const tasksLoadOptions: GetLoadOptions = useCallback(
    () =>
      getTasksLoadOptions({
        fetchLazyQuery: fetchTasks,
        scope: { name: TaskScopeNameEnum.Active },
        selectedSiteIds: [],
      }),
    [fetchTasks],
  );

  // Mutation
  const [importCreateMutation, { loading, error: importCreateError }] =
    useEdmDocumentationImportCreateMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  // Whether AI spend is refused right now — the enhancements are dropped from
  // the payload if so, rather than losing the whole import to them.
  const aiBudget = useSharedAiBudget();

  const onSubmit = useCallback(
    (values: Values) => {
      const attributes = getPreparedImportData(values, getTaskGroupKey, {
        aiBlocked: !!aiBudget?.blockedReason,
      });
      if (!attributes) return;
      importCreateMutation({
        variables: { attributes },
        onCompleted: (res) => {
          const backgroundJob = _get(res, 'data.backgroundJob') as
            BackgroundJob | undefined;
          if (backgroundJob) {
            jobEventBus.emit(EventBusEvents.JobCreated, backgroundJob);
          }
          toast({ content: t('documents-import-started-toast') });
          navigate(APP_URLS.dashboard.documentation.index.getPathWithQuery());
        },
      });
    },
    [
      aiBudget?.blockedReason,
      getTaskGroupKey,
      importCreateMutation,
      navigate,
      t,
    ],
  );

  return {
    approverLoadOptions,
    areasLoadOptions,
    categoriesLoadOptions,
    equipmentLoadOptions,
    formTemplatesLoadOptions,
    graphQLErrors: getGraphQLErrors(importCreateError),
    loading,
    onCancel,
    onSubmit,
    sectorsLoadOptions,
    sitesLoadOptions,
    tasksLoadOptions,
  };
}
